import { FC, FormEvent, useEffect, useState } from "react";
import {
  createResource,
  deleteResource,
  listResource,
  updateResource,
} from "data/adminApi";
import ConfirmDialog from "components/Admin/ConfirmDialog";
import FileUploader from "components/Admin/FileUploader";
import { PencilSquareIcon, TrashIcon } from "@heroicons/react/24/outline";

interface Project {
  id: number;
  name: string;
  description: string;
  url?: string;
  images?: string;
  date: string;
}

const empty = {
  name: "",
  description: "",
  url: "",
  images: "",
  date: "",
};

const AdminProjects: FC = () => {
  const [items, setItems] = useState<Project[]>([]);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState<number | null>(null);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  const load = async () => setItems(await listResource<Project>("/projects/"));

  useEffect(() => {
    load().catch(() => setMessage("Failed to load projects."));
  }, []);

  const save = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editId) await updateResource(`/projects/${editId}/`, form);
      else await createResource("/projects/", form);
      setForm(empty);
      setEditId(null);
      await load();
      setMessage("Project saved.");
    } catch {
      setMessage("Could not save project.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Projects</h1>
        <p className="text-sm text-neutral-500 mt-1">
          Manage portfolio projects shown on the website.
        </p>
      </div>
      {message && (
        <div className="rounded-xl bg-primary-50 text-primary-800 text-sm px-4 py-3">
          {message}
        </div>
      )}

      <form
        onSubmit={save}
        className="rounded-2xl border bg-white dark:bg-neutral-900 p-5 space-y-3"
      >
        <input
          required
          placeholder="Project name"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          className="w-full h-11 px-4 rounded-xl border"
        />
        <textarea
          required
          rows={4}
          placeholder="Description"
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
          className="w-full px-4 py-3 rounded-xl border"
        />
        <div className="grid sm:grid-cols-2 gap-3">
          <input
            placeholder="Project URL"
            value={form.url}
            onChange={(e) => setForm({ ...form, url: e.target.value })}
            className="h-11 px-4 rounded-xl border"
          />
          <input
            required
            placeholder="Date (e.g. 2024)"
            value={form.date}
            onChange={(e) => setForm({ ...form, date: e.target.value })}
            className="h-11 px-4 rounded-xl border"
          />
        </div>
        <input
          placeholder="Image URL"
          value={form.images}
          onChange={(e) => setForm({ ...form, images: e.target.value })}
          className="w-full h-11 px-4 rounded-xl border"
        />
        <FileUploader
          folder="projects"
          label="Upload project image"
          onUploaded={(url) => setForm({ ...form, images: url })}
        />
        <button
          type="submit"
          disabled={loading}
          className="h-10 px-4 rounded-xl bcr-btn-primary text-sm font-semibold"
        >
          {editId ? "Update" : "Create"}
        </button>
      </form>

      <div className="rounded-2xl border bg-white dark:bg-neutral-900 overflow-hidden">
        <ul className="divide-y dark:divide-neutral-800">
          {items.map((p) => (
            <li
              key={p.id}
              className="px-5 py-4 flex items-center justify-between gap-3"
            >
              <div className="flex items-center gap-3 min-w-0">
                {p.images && (
                  <img
                    src={p.images}
                    alt=""
                    className="w-14 h-14 rounded-lg object-cover shrink-0"
                  />
                )}
                <div className="min-w-0">
                  <p className="font-semibold truncate">{p.name}</p>
                  <p className="text-xs text-neutral-500">{p.date}</p>
                </div>
              </div>
              <div className="flex gap-2 shrink-0">
                <button
                  type="button"
                  onClick={() => {
                    setEditId(p.id);
                    setForm({
                      name: p.name,
                      description: p.description,
                      url: p.url || "",
                      images: p.images || "",
                      date: p.date,
                    });
                  }}
                  className="p-2 rounded-lg hover:bg-neutral-100"
                >
                  <PencilSquareIcon className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setDeleteId(p.id)}
                  className="p-2 rounded-lg hover:bg-red-50 text-red-600"
                >
                  <TrashIcon className="w-4 h-4" />
                </button>
              </div>
            </li>
          ))}
        </ul>
      </div>

      <ConfirmDialog
        open={!!deleteId}
        title="Delete project?"
        message="This project will be removed from the website."
        loading={loading}
        onClose={() => setDeleteId(null)}
        onConfirm={async () => {
          if (!deleteId) return;
          setLoading(true);
          try {
            await deleteResource(`/projects/${deleteId}/`);
            await load();
          } finally {
            setLoading(false);
            setDeleteId(null);
          }
        }}
      />
    </div>
  );
};

export default AdminProjects;
